import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../lib/format";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { FA_ISI_ROLES } from "../lib/roles";
import { tampilHp } from "../lib/nomorHp";

//: Cermin `URUT_SAH_CUSTOMER` di backend/routers/customers.py.
const URUT_SAH = ["created_at", "name", "city"];

const KOLOM_CSV = [
  { kunci: "name", judul: "Nama" },
  { kunci: "phone", judul: "Telepon", ambil: (c) => tampilHp(c.phone) },
  { kunci: "store", judul: "Cabang asal", ambil: (c) => c.store?.name || "" },
  { kunci: "city", judul: "Kota", ambil: (c) => c.city || "" },
  { kunci: "created_at", judul: "Dibuat", ambil: (c) => formatDate(c.created_at) },
];
// K206 / VONIS-HAK-BACA-ISI-FA: telepon customer hanya untuk Owner/KC/CS (backend tidak mengirimnya ke peran lain).
const KOLOM_CSV_TANPA_KONTAK = KOLOM_CSV.filter((k) => k.kunci !== "phone");

// Customer — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
// Cari (nama / telepon), urut, saringan cabang asal, paginasi, dan unduhan dikerjakan
// server; posisi halaman tersimpan di alamat, jadi Kembali dari rincian customer
// memulihkan halaman + kata cari yang sama. Cakupan wilayah dari backend (`_scope_filter`).
export default function Customers() {
  const { profile, isOwner, isKepalaCabang } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [regions, setRegions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const canFilterStore = isOwner || isKepalaCabang;
  // K206: kontak customer + tambah customer hanya Owner/KC/CS — Admin Produksi/Gudang/Purchasing melihat nama + kota.
  const bolehKontak = FA_ISI_ROLES.includes(profile?.role?.code);
  const namaStore = (id) => stores.find((s) => s.id === id)?.name || "cabang";

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      store: { label: "Cabang asal", param: "store_id", tampil: namaStore },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan,
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/customers", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/customers", { params: bangunParams() });
      setItems(res.data?.items || []);
      setTotal(typeof res.data?.total === "number" ? res.data.total : 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat data customer"));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => {
    Promise.all([api.get("/stores"), api.get("/regions")])
      .then(([s, r]) => { setStores(s.data); setRegions(r.data); })
      .catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
  }, []);

  useEffect(() => { load(); }, [load]);

  const adaSaringan = daftar.cari.trim() || daftar.saringanAktif.length;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Customer</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelola data customer dan riwayat FA mereka.</p>
        </div>
        {bolehKontak && (
          <Button data-testid="customer-new-btn" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1" /> Tambah Customer
          </Button>
        )}
      </div>

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder={bolehKontak ? "Cari nama atau no. telepon..." : "Cari nama..."}
                      testId="customer-palang"
                      ekspor={{ nama: "customer", kolom: bolehKontak ? KOLOM_CSV : KOLOM_CSV_TANPA_KONTAK, ambilHalaman }}>
          {canFilterStore && (
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={daftar.nilaiSaringan.store}
              onChange={(e) => daftar.setSaringan("store", e.target.value)}
              aria-label="Saring cabang asal"
              data-testid="customer-store-filter"
            >
              <option value="">Semua cabang</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </PalangDaftar>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="name" className="px-3 py-2">Nama</ThUrut>
                {bolehKontak && <th className="px-3 py-2">Telepon</th>}
                <th className="px-3 py-2">Cabang Asal</th>
                <ThUrut daftar={daftar} kunci="city" className="px-3 py-2">Kota</ThUrut>
                <ThUrut daftar={daftar} kunci="created_at" className="px-3 py-2">Dibuat</ThUrut>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan={bolehKontak ? 6 : 5} data-testid="customers-loading" className="px-3 py-8 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan={bolehKontak ? 6 : 5} className="px-3 py-8 text-center text-muted-foreground" data-testid="customers-kosong">
                  {adaSaringan ? "Tidak ada customer yang cocok dengan pencarian atau saringan ini." : "Belum ada customer."}
                </td></tr>
              )}
              {daftar.tampil.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  className="border-t border-border hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/customers/${c.id}`)}
                >
                  {/* KONTRAS-01: nol kelas warna, SENGAJA. Sel ini
                      mewarisi `text-card-foreground` dari <Card>, jadi ia
                      ikut bertukar saat tema berganti — pola yang sama
                      dengan kolom NO. FA di FAList.jsx (c96562f).
                      Warna teks gelap literal yang lama praktis tak terbaca di
                      atas kartu gelap (rasio 1.02). */}
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  {bolehKontak && <td className="px-3 py-2 text-muted-foreground">{tampilHp(c.phone) || "—"}</td>}
                  <td className="px-3 py-2 text-muted-foreground">{c.store?.name || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.city || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{formatDate(c.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <ExternalLink size={14} className="inline text-muted-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="customer-paginasi" />
      </Card>

      {showCreate && (
        <CustomerCreateModal
          onClose={() => setShowCreate(false)}
          onSaved={(c) => { setShowCreate(false); navigate(`/customers/${c.id}`); }}
          stores={stores}
          regions={regions}
          profile={profile}
        />
      )}
    </div>
  );
}

function CustomerCreateModal({ onClose, onSaved, stores, regions, profile }) {
  const navigate = useNavigate();
  const role = profile?.role?.code;
  const initRegion = role === "CS" || role === "KEPALA_CABANG" ? profile?.region_id : "";
  const initStore = role === "CS" ? profile?.store_id : "";

  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", city: "",
    region_id: initRegion || "", store_origin_id: initStore || "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const storesFiltered = form.region_id ? stores.filter((s) => s.region_id === form.region_id) : stores;

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama wajib diisi"); return; }
    // T1: cabang asal customer wajib (backend CABANG_ASAL_WAJIB) — CS otomatis cabangnya sendiri.
    if ((role === "OWNER" || role === "KEPALA_CABANG") && (!form.region_id || !form.store_origin_id)) {
      toast.error("Pilih wilayah dan cabang asal customer dulu.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        phone: form.phone || null,
        email: form.email ? form.email.trim().toLowerCase() : null,
        address: form.address || null,
        city: form.city || null,
        notes: form.notes || null,
      };
      // Only Owner / Kepala Cabang may override region/store; CS uses backend default.
      if (role === "OWNER" || role === "KEPALA_CABANG") {
        if (form.region_id) body.region_id = form.region_id;
        if (form.store_origin_id) body.store_origin_id = form.store_origin_id;
      }
      const res = await api.post("/customers", body);
      toast.success("Customer berhasil dibuat");
      onSaved(res.data);
    } catch (e) {
      // K221 (BUSINESS-97): nomor HP sudah dipakai customer lain di wilayah ini → arahkan ke data yang ada.
      const d = e?.response?.data?.detail;
      if (d?.error === "CUSTOMER_HP_SUDAH_ADA" && d.existing_customer_id) {
        toast.error(d.message, {
          duration: 12000,
          action: { label: "Buka data customer", onClick: () => navigate(`/customers/${d.existing_customer_id}`) },
        });
        return;
      }
      toast.error(apiErrorMessage(e, "Gagal menyimpan customer"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="customer-create-modal">
        <DialogHeader><DialogTitle>Tambah Customer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <div className="md:col-span-2">
            <Label htmlFor="cname">Nama <span className="text-danger">*</span></Label>
            <Input id="cname" data-testid="customer-form-name" value={form.name} onChange={(e) => upd("name", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cphone">No. Telepon</Label>
            <Input id="cphone" data-testid="customer-form-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="08xx..." />
          </div>
          <div>
            <Label htmlFor="cemail">Email</Label>
            <Input id="cemail" data-testid="customer-form-email" value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="opsional" />
          </div>
          <div>
            <Label htmlFor="ccity">Kota</Label>
            <Input id="ccity" data-testid="customer-form-city" value={form.city} onChange={(e) => upd("city", e.target.value)} />
          </div>
          {role === "OWNER" || role === "KEPALA_CABANG" ? (
            <>
              <div>
                <Label>Wilayah</Label>
                <Select value={form.region_id} onValueChange={(v) => upd("region_id", v)} disabled={role === "KEPALA_CABANG"}>
                  <SelectTrigger data-testid="customer-form-region"><SelectValue placeholder="Pilih wilayah" /></SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cabang Asal <span className="text-danger">*</span></Label>
                <Select value={form.store_origin_id} onValueChange={(v) => upd("store_origin_id", v)} disabled={!form.region_id}>
                  <SelectTrigger data-testid="customer-form-store"><SelectValue placeholder={form.region_id ? "Pilih cabang" : "Pilih wilayah dulu"} /></SelectTrigger>
                  <SelectContent>
                    {storesFiltered.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <div className="md:col-span-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded border border-border">
              Customer akan otomatis terikat ke cabang Anda ({stores.find((s) => s.id === profile?.store_id)?.name || "—"}).
            </div>
          )}
          <div className="md:col-span-2">
            <Label htmlFor="caddr">Alamat</Label>
            <Textarea id="caddr" data-testid="customer-form-address" rows={2} value={form.address} onChange={(e) => upd("address", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="cnotes">Catatan</Label>
            <Textarea id="cnotes" data-testid="customer-form-notes" rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="customer-form-cancel">Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="customer-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { CustomerCreateModal };
